import { useState } from "react";
import { toast } from "sonner";
import { Trash2, Loader2, Pencil, Check, X, GripVertical, ChevronLeft, ChevronRight } from "lucide-react";
import { api, apiError, resolveUrl } from "@/lib/api";

const iconBtn =
    "grid h-8 w-8 shrink-0 place-items-center border border-white/25 text-white transition-colors disabled:opacity-30";

const TitleEditor = ({ photo, index, onSaved, onCancel }) => {
    const [value, setValue] = useState(photo.title || "");
    const [busy, setBusy] = useState(false);
    const save = async () => {
        setBusy(true);
        try {
            const { data } = await api.patch(`/admin/gallery/${photo.id}`, { title: value });
            onSaved(data);
            toast.success("Titolo aggiornato.");
        } catch (err) {
            toast.error(apiError(err, "Salvataggio non riuscito."));
            setBusy(false);
        }
    };
    return (
        <span className="flex w-full items-center gap-1">
            <input
                autoFocus
                data-testid={`admin-photo-title-input-${index}`}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") onCancel(); }}
                className="min-w-0 flex-1 border border-tl-orange bg-tl-dark px-2 py-1 text-xs text-white outline-none"
                placeholder="Titolo foto"
            />
            <button type="button" data-testid={`admin-photo-title-save-${index}`} onClick={save} disabled={busy} className={`${iconBtn} hover:border-tl-orange hover:text-tl-orange`} aria-label="Salva titolo">
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={14} />}
            </button>
            <button type="button" data-testid={`admin-photo-title-cancel-${index}`} onClick={onCancel} className={iconBtn} aria-label="Annulla">
                <X size={14} />
            </button>
        </span>
    );
};

const DeleteControls = ({ photo, index, onDeleted }) => {
    const [confirm, setConfirm] = useState(false);
    const [busy, setBusy] = useState(false);
    const remove = async () => {
        setBusy(true);
        try {
            await api.delete(`/admin/gallery/${photo.id}`);
            onDeleted(photo.id);
            toast.success("Foto eliminata.");
        } catch (err) {
            toast.error(apiError(err, "Eliminazione non riuscita."));
            setBusy(false);
            setConfirm(false);
        }
    };
    if (confirm) {
        return (
            <span className="flex shrink-0 items-center gap-1">
                <button type="button" data-testid={`admin-photo-confirm-delete-${index}`} onClick={remove} disabled={busy} className="bg-red-500 px-2.5 py-1.5 font-mono-tl text-[10px] uppercase tracking-[0.15em] text-white hover:bg-red-600 disabled:opacity-60">
                    {busy ? <Loader2 size={12} className="animate-spin" /> : "Conferma"}
                </button>
                <button type="button" data-testid={`admin-photo-cancel-delete-${index}`} onClick={() => setConfirm(false)} className="border border-white/30 px-2.5 py-1.5 font-mono-tl text-[10px] uppercase tracking-[0.15em] text-white hover:border-white">
                    No
                </button>
            </span>
        );
    }
    return (
        <button type="button" data-testid={`admin-photo-delete-${index}`} onClick={() => setConfirm(true)} aria-label="Elimina foto" className={`${iconBtn} hover:border-red-400 hover:text-red-400`}>
            <Trash2 size={14} />
        </button>
    );
};

export const AdminPhotoCard = ({ photo, index, total, onDeleted, onUpdated, onMove, dragProps, dragging }) => {
    const [editing, setEditing] = useState(false);
    return (
        <figure
            data-testid={`admin-photo-${index}`}
            {...dragProps}
            className={`group relative aspect-[4/3] overflow-hidden border bg-tl-card transition-[opacity,border-color] ${
                dragging ? "border-tl-orange opacity-40" : "border-white/10"
            }`}
        >
            <img src={resolveUrl(photo.url)} alt={photo.title} loading="lazy" draggable={false} className="h-full w-full object-cover" />
            <span className="absolute left-2 top-2 flex items-center gap-1.5 bg-tl-dark/85 px-2 py-1 font-mono-tl text-[10px] text-tl-orange">
                <GripVertical size={12} className="cursor-grab text-white/60" /> {String(index + 1).padStart(2, "0")}
            </span>
            <span className="absolute right-2 top-2 flex items-center gap-1">
                <button type="button" data-testid={`admin-photo-move-left-${index}`} onClick={() => onMove(index, index - 1)} disabled={index === 0} aria-label="Sposta prima" className={`${iconBtn} bg-tl-dark/80 hover:border-tl-orange`}>
                    <ChevronLeft size={14} />
                </button>
                <button type="button" data-testid={`admin-photo-move-right-${index}`} onClick={() => onMove(index, index + 1)} disabled={index === total - 1} aria-label="Sposta dopo" className={`${iconBtn} bg-tl-dark/80 hover:border-tl-orange`}>
                    <ChevronRight size={14} />
                </button>
            </span>
            <figcaption className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-gradient-to-t from-tl-dark/95 to-transparent p-3">
                {editing ? (
                    <TitleEditor photo={photo} index={index} onSaved={(p) => { onUpdated(p); setEditing(false); }} onCancel={() => setEditing(false)} />
                ) : (
                    <>
                        <span data-testid={`admin-photo-title-${index}`} className="truncate font-mono-tl text-[10px] uppercase tracking-[0.15em] text-white/80">
                            {photo.title || "—"}
                        </span>
                        <span className="flex shrink-0 items-center gap-1">
                            <button type="button" data-testid={`admin-photo-edit-${index}`} onClick={() => setEditing(true)} aria-label="Modifica titolo" className={`${iconBtn} hover:border-tl-orange hover:text-tl-orange`}>
                                <Pencil size={13} />
                            </button>
                            <DeleteControls photo={photo} index={index} onDeleted={onDeleted} />
                        </span>
                    </>
                )}
            </figcaption>
        </figure>
    );
};
